/**
 * ChunksViewer - Componente para visualizar chunks seleccionados en el panel Visor
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, FileText, MapPin, ExternalLink, Copy } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface Chunk {
  id: string;
  text: string;
  page_number?: number;
  document_id?: string;
  document_name?: string;
  chunk_id?: string;
  [key: string]: any;
}

interface ChunksViewerProps {
  chunks: Chunk[];
  onOpenInPDFViewer?: (chunk: Chunk) => void;
  onCopy?: (text: string) => void;
}

const ChunksViewer: React.FC<ChunksViewerProps> = ({ chunks, onOpenInPDFViewer, onCopy }) => {
  const { theme } = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!chunks || chunks.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <FileText
            className={`w-12 h-12 mx-auto mb-4 ${theme.colors.text.secondary} opacity-50`}
          />
          <p className={`text-sm ${theme.colors.text.secondary}`}>No hay chunks seleccionados</p>
        </div>
      </div>
    );
  }

  const currentChunk = chunks[currentIndex];
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < chunks.length - 1;

  const handleCopyContent = () => {
    if (currentChunk.text && onCopy) {
      onCopy(currentChunk.text);
      navigator.clipboard.writeText(currentChunk.text);
    }
  };

  const maxInitialChars = 600;
  const shouldShowExpand = currentChunk.text && currentChunk.text.length > maxInitialChars;
  const displayContent = isExpanded
    ? currentChunk.text
    : shouldShowExpand
      ? currentChunk.text.substring(0, maxInitialChars) + '...'
      : currentChunk.text;

  return (
    <div
      className={`h-full flex flex-col ${theme.colors.bg.primary} overflow-y-auto scrollbar-thin`}
    >
      {/* Header con navegación */}
      <div
        className={`flex-shrink-0 border-b ${theme.colors.border.primary} px-4 py-2 flex items-center justify-between`}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
            disabled={!canGoPrev}
            className={`p-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
            title="Chunk anterior"
          >
            <ChevronLeft size={16} />
          </button>
          <span className={`text-xs font-medium ${theme.colors.text.primary}`}>
            {currentIndex + 1} / {chunks.length}
          </span>
          <button
            onClick={() => setCurrentIndex((prev) => Math.min(chunks.length - 1, prev + 1))}
            disabled={!canGoNext}
            className={`p-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} transition-colors disabled:opacity-30 disabled:cursor-not-allowed`}
            title="Chunk siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {currentChunk.document_name && (
          <div className="flex items-center gap-2">
            <FileText className={`w-3.5 h-3.5 ${theme.colors.text.secondary}`} />
            <span className={`text-xs ${theme.colors.text.secondary} truncate max-w-[200px]`}>
              {currentChunk.document_name}
            </span>
          </div>
        )}
      </div>

      {/* Contenido del chunk */}
      <div className="flex-1 p-4 space-y-4">
        {/* Metadata */}
        {currentChunk.page_number && (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-nb-error flex-shrink-0" />
            <span className={`text-sm font-medium ${theme.colors.text.primary}`}>
              Página {currentChunk.page_number}
            </span>
          </div>
        )}

        {/* Contenido del chunk */}
        <div
          className={`${theme.colors.bg.secondary} border ${theme.colors.border.primary} rounded-lg p-4`}
        >
          <div
            className={`text-sm ${theme.colors.text.primary} leading-relaxed whitespace-pre-wrap`}
          >
            {displayContent}
          </div>
          {shouldShowExpand && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`mt-2 text-xs font-medium ${theme.colors.text.secondary} hover:${theme.colors.text.primary} transition-colors underline`}
            >
              {isExpanded ? '↑ Leer menos' : '↓ Leer más'}
            </button>
          )}
        </div>

        {/* Metadata adicional */}
        {currentChunk.chunk_id && (
          <div className={`text-xs ${theme.colors.text.secondary}`}>
            ID: {currentChunk.chunk_id}
          </div>
        )}
      </div>

      {/* Footer con acciones */}
      <div
        className={`flex-shrink-0 border-t ${theme.colors.border.primary} p-3 flex justify-between gap-2 bg-opacity-50 ${theme.colors.bg.primary}`}
      >
        <div className="flex gap-2">
          <button
            onClick={handleCopyContent}
            className={`px-3 py-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-xs font-medium flex items-center gap-2`}
          >
            <Copy size={14} />
            Copiar
          </button>
          {onOpenInPDFViewer && (
            <button
              onClick={() => onOpenInPDFViewer(currentChunk)}
              className={`px-3 py-1.5 rounded ${theme.colors.bg.secondary} ${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors text-xs font-medium flex items-center gap-2`}
            >
              <ExternalLink size={14} />
              Ver en PDF
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChunksViewer;
